import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  cicloVigente,
  completo,
  trasDescanso,
  trasPomodoro,
  type TipoDescanso,
} from './foco-ciclo';

/**
 * EL TEMPORIZADOR VIVE FUERA DE SU PANTALLA.
 *
 * Si el estado del pomodoro colgara del componente de Foco, navegar a Hoy para
 * marcar algo lo mataría. Aquí está en un store global y en `zustand`, que es
 * exactamente el caso de uso que el PRD 15.3 le reserva: estado de interfaz
 * efímero, no datos de dominio. Los datos de dominio están en IndexedDB.
 *
 * Y el tiempo NO se cuenta sumando tics. Se cuenta restando marcas de tiempo
 * reales: un `setInterval` en una pestaña de fondo se ralentiza o se para, y un
 * temporizador que pierde minutos cuando bloqueas el móvil es un temporizador
 * roto. El intervalo solo repinta; la verdad es `endsAt - Date.now()`.
 *
 * Y SOBREVIVE A UNA RECARGA: se persiste en `localStorage`. Todo son marcas de
 * tiempo de pared, así que al rehidratar el reloj sigue donde debe, y una sesión
 * que terminó con la app cerrada se cierra y se guarda al volver
 * (`foco-vigia.tsx`).
 */

export type FocusPhase = 'idle' | 'running' | 'paused' | 'done';

/** Lo que corre: un pomodoro o un descanso. El descanso no se guarda ni puntúa. */
export type TipoSesion = 'foco' | 'descanso';

/** Lo que `cerrar` devuelve: la sesión tal como terminó, antes de vaciarla. */
export type SesionCerrada = {
  tipo: TipoSesion;
  habitId: string | null;
  areaId: string | null;
  label: string;
  startedAt: string | null;
  plannedMinutes: number;
  workedMs: number;
};

type FocusState = {
  phase: FocusPhase;
  /** Minutos planificados. */
  plannedMinutes: number;
  label: string;
  habitId: string | null;
  areaId: string | null;
  /** Marca de tiempo en la que termina, en ms. */
  endsAt: number | null;
  /** Cuando está en pausa: milisegundos que quedaban. */
  remainingMs: number;
  startedAt: string | null;
  /** Milisegundos efectivamente trabajados, sin contar las pausas. */
  workedMs: number;
  /** Identifica la sesión ante el servidor: es la clave de su aviso de fin. */
  sesionId: string | null;
  tipo: TipoSesion;
  /** Qué descanso es, si lo que corre es un descanso. */
  descanso: TipoDescanso | null;
  /** Pomodoros completos de la tanda en curso (ver `foco-ciclo.ts`). */
  ciclo: number;
  /** Cuándo cerró el último pomodoro o descanso: decide si la tanda caducó. */
  ultimoFinMs: number | null;
  /** El descanso que la pantalla ofrece tras un pomodoro completo. */
  oferta: TipoDescanso | null;

  start: (args: {
    plannedMinutes: number;
    label: string;
    habitId: string | null;
    areaId: string | null;
  }) => void;
  startBreak: (descanso: TipoDescanso, minutos: number) => void;
  /** No tomar el descanso ofrecido. Saltarse el largo cierra la tanda igual. */
  skipBreak: () => void;
  /**
   * Cierra la sesión terminada: avanza la tanda, vacía la sesión y la devuelve
   * para guardarla. Lo llama `VigiaFoco`; vaciar y avanzar en un solo `set` evita
   * que un segundo disparo la cuente dos veces.
   */
  cerrar: (porCiclo: number) => SesionCerrada | null;
  reiniciarCiclo: () => void;
  /** Si la tanda caducó (ver `TANDA_CADUCA_MS`), la vacía. Se llama al abrir Foco. */
  revisarTanda: () => void;
  pause: () => void;
  resume: () => void;
  finish: () => void;
  reset: () => void;
  tick: () => void;
};

/**
 * Lo trabajado, sin las pausas. El restante solo baja mientras corre, así que lo
 * trabajado es lo planificado menos lo que falta. Sumar tramos contra un
 * `remainingMs` que el tic pisa cada 250 ms contaba solo el último tic.
 */
function trabajado(plannedMinutes: number, left: number): number {
  return Math.max(0, plannedMinutes * 60_000 - left);
}

/** La sesión vacía. La tanda (`ciclo`, `oferta`…) no es de la sesión: sobrevive. */
const SIN_SESION = {
  phase: 'idle',
  endsAt: null,
  remainingMs: 0,
  startedAt: null,
  workedMs: 0,
  sesionId: null,
  label: '',
  habitId: null,
  areaId: null,
  tipo: 'foco',
  descanso: null,
} as const;

export const useFocus = create<FocusState>()(
  persist(
    (set, get) => ({
      phase: 'idle',
      plannedMinutes: 25,
      label: '',
      habitId: null,
      areaId: null,
      endsAt: null,
      remainingMs: 0,
      startedAt: null,
      workedMs: 0,
      sesionId: null,
      tipo: 'foco',
      descanso: null,
      ciclo: 0,
      ultimoFinMs: null,
      oferta: null,

      start: ({ plannedMinutes, label, habitId, areaId }) =>
        set({
          tipo: 'foco',
          descanso: null,
          oferta: null,
          phase: 'running',
          plannedMinutes,
          label,
          habitId,
          areaId,
          endsAt: Date.now() + plannedMinutes * 60_000,
          remainingMs: plannedMinutes * 60_000,
          startedAt: new Date().toISOString(),
          workedMs: 0,
          sesionId: crypto.randomUUID(),
        }),

      startBreak: (descanso, minutos) =>
        set({
          phase: 'running',
          tipo: 'descanso',
          descanso,
          oferta: null,
          plannedMinutes: minutos,
          label: descanso === 'largo' ? 'Descanso largo' : 'Descanso corto',
          habitId: null,
          areaId: null,
          endsAt: Date.now() + minutos * 60_000,
          remainingMs: minutos * 60_000,
          startedAt: new Date().toISOString(),
          workedMs: 0,
          sesionId: crypto.randomUUID(),
        }),

      skipBreak: () => {
        const { oferta, ciclo } = get();
        set({ oferta: null, ciclo: oferta === 'largo' ? 0 : ciclo });
      },

      cerrar: (porCiclo) => {
        const s = get();
        if (s.phase !== 'done') return null;
        const sesion: SesionCerrada = {
          tipo: s.tipo,
          habitId: s.habitId,
          areaId: s.areaId,
          label: s.label,
          startedAt: s.startedAt,
          plannedMinutes: s.plannedMinutes,
          workedMs: s.workedMs,
        };
        const ahora = Date.now();
        const ciclo =
          s.tipo === 'foco'
            ? trasPomodoro(s, completo(s.plannedMinutes, s.workedMs), porCiclo, ahora)
            : { ...trasDescanso(s, s.descanso ?? 'corto', ahora), oferta: null };
        set({ ...SIN_SESION, ...ciclo });
        return sesion;
      },

      reiniciarCiclo: () => set({ ciclo: 0, ultimoFinMs: null, oferta: null }),

      revisarTanda: () => {
        const { ciclo, ultimoFinMs, phase } = get();
        if (phase !== 'idle' || ciclo === 0) return;
        if (cicloVigente(ciclo, ultimoFinMs, Date.now()) === 0) get().reiniciarCiclo();
      },

      pause: () => {
        const { endsAt, phase, plannedMinutes } = get();
        if (phase !== 'running' || endsAt === null) return;
        const left = Math.max(0, endsAt - Date.now());
        set({
          phase: 'paused',
          remainingMs: left,
          endsAt: null,
          workedMs: trabajado(plannedMinutes, left),
        });
      },

      resume: () => {
        const { remainingMs, phase } = get();
        if (phase !== 'paused') return;
        set({ phase: 'running', endsAt: Date.now() + remainingMs });
      },

      finish: () => {
        const { endsAt, remainingMs, plannedMinutes } = get();
        const left = endsAt === null ? remainingMs : Math.max(0, endsAt - Date.now());
        set({
          phase: 'done',
          remainingMs: left,
          endsAt: null,
          workedMs: trabajado(plannedMinutes, left),
        });
      },

      reset: () => set(SIN_SESION),

      tick: () => {
        const { phase, endsAt } = get();
        if (phase !== 'running' || endsAt === null) return;
        if (Date.now() >= endsAt) get().finish();
        else set({ remainingMs: Math.max(0, endsAt - Date.now()) });
      },
    }),
    {
      name: 'selflevel-foco',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        phase: s.phase,
        plannedMinutes: s.plannedMinutes,
        label: s.label,
        habitId: s.habitId,
        areaId: s.areaId,
        endsAt: s.endsAt,
        remainingMs: s.remainingMs,
        startedAt: s.startedAt,
        workedMs: s.workedMs,
        sesionId: s.sesionId,
        tipo: s.tipo,
        descanso: s.descanso,
        ciclo: s.ciclo,
        ultimoFinMs: s.ultimoFinMs,
        oferta: s.oferta,
      }),
      // El `remainingMs` guardado es el del último tic antes de cerrar: se recalcula.
      onRehydrateStorage: () => (state) => state?.tick(),
    },
  ),
);

/** Si hay una sesión en marcha. Los acontecimientos se encolan mientras sea true. */
export function useFocusRunning(): boolean {
  // Un descanso no retiene nada: es justo el momento de ver un hito.
  return useFocus((s) => s.phase === 'running' && s.tipo === 'foco');
}

/** Si hay una sesión abierta, corriendo o en pausa. */
export function useFocusOpen(): boolean {
  return useFocus((s) => s.phase === 'running' || s.phase === 'paused');
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
